import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { CancelToken } from "axios";
import DatePicker from "react-datepicker";

import "./ItinDetailPage.css";
import { get, post } from "../common/api";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { useAlert } from "react-alert";
import { PriceLabel } from "../common/PriceLabel";
import FiveStarComponent from "../common/FiveStarComponent";

import { ReactComponent as EditIcon } from "../logo/Edit.svg";
import { ReactComponent as DeleteIcon } from "../logo/Delete.svg";
import { ReactComponent as ShareIcon } from "../logo/Share.svg";

import { Link } from "react-router-dom";
import BucketListAddition from "./BucketListAddition";
import { extractDateFromDateTimeString, generateShareItinUrl } from "./utils";

export const ItinDetailPageMode = {
  read: "read",
  edit: "edit",
  create: "create",
  public: "public",
};

export function ItinDetailPage(props) {
  let { username, id } = useParams();
  const auth = useSelector((state) => state.auth);
  const history = useHistory();
  const alert = useAlert();

  const [isLoading, setIsLoading] = useState(true);
  const [itinPoiList, setItinPoiList] = useState(null);
  const [itineraryName, setItineraryName] = useState("");
  const [deletedPoi, setDeletedPoi] = useState([]);
  const [editedPois, setEditedPois] = useState([]);
  const [allPois, setAllPois] = useState([]);
  const [bucketlists, setBucketlists] = useState([]);
  const [addedPoi, setAddedPoi] = useState([]);
  const [poiTemp, setPoiTemp] = useState([]);
  const [createdBy, setCreatedBy] = useState({});
  const [error, setError] = useState(null);

  const isInvalidRange = useCallback((array) => {
    for (let i = 0; i < array.length; i++) {
      if (strToDate(array[i]['start_time']) >= strToDate(array[i]['end_time'])){
        return true
      }
    }
    return false
  }, [])

  const timeOverlap = useCallback((rangeA, rangeB) => {
    let startA = strToDate(rangeA['start_time'])
    let endA   = strToDate(rangeA['end_time'])
    let startB = strToDate(rangeB['start_time'])
    let endB   = strToDate(rangeB['end_time'])

    return startA < endB && startB < endA;
  }, [])

  const checkOverlap = useCallback((array) => {
    for (let i = 0; i < array.length; i++) {
      for (let j = i+1; j < array.length; j++){
        if (timeOverlap(array[i], array[j])) {
          return true
        }
      }
    }
    return false
  }, [timeOverlap])

  const checkConflictAll = useCallback((value) => {
    if(isInvalidRange(allPois)){
      alert.error("Waktu mulai harus lebih awal dari Waktu selesai")
      return true
    }
    else if (checkOverlap(allPois)){
      alert.error("Jadwal Anda bentrok")
      return true
    }
    return false
  }, [checkOverlap, isInvalidRange, allPois, alert])

  const getBucketlists = async () => {
    const token = auth.token;
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    if (token) {
      config.headers["Authorization"] = `Token ${token}`;
    }

    try {
      const res = await get("/api/bucket-list/", config);
      const results = res.data;
      const bl = results.map((result) => ({
        ...result.poi,
        start_time: new Date(),
        end_time: new Date(),
      }));
      setPoiTemp(itinPoiList);
      if (poiTemp.length !== 0) {
        let currentPoi = poiTemp.map((itin) => itin["poi"]["id"]);
        const bl2 = bl.filter((res) => currentPoi.indexOf(res["id"]) === -1);
        setBucketlists(bl2);
      }
    } catch (err) {
      console.log(err);
    }
  };
  
  useEffect(() => {
    const source = CancelToken.source();
    if (props.mode === ItinDetailPageMode.public) {
      const fetchPublicPoiItin = async () => {
        try {
          const res = await get(`/api/share-poi-itin/${username}/${id}/`, {
            cancelToken: source.cancelToken,
            headers: {
              "Content-Type": "application/json",
            },
          });
          setItinPoiList(res.data.poi);
          setItineraryName(res.data.poi[0].trip.name);

          const createdByData = res.data.created_by;
          let fullName = [
            createdByData.first_name,
            createdByData.last_name,
          ].join(" ");
          // if the fullName is empty, we should use username instead
          if (fullName === " ") {
            setCreatedBy(createdByData.username);
          } else {
            setCreatedBy(fullName);
          }
        } catch (e) {
          const message =
            e.response?.data?.message ||
            "Terjadi suatu kesalahan, silahkan coba lagi.";
          alert.error(message);
          setError(e);
          if (e.response?.status === 404) history.push("/");
        }
        setIsLoading(false);
      };
      fetchPublicPoiItin();
      return;
    }

    if (!auth.token) {
      alert.info(
        "Silahkan login terlebih dahulu untuk melihat page Itinerary."
      );
      history.push("/login");
      return;
    }

    // No need to do fetching again if this is a create mode
    if (props.mode === ItinDetailPageMode.create) {
      const params = history.location.state.itinPoiList;
      setItinPoiList(params);
      getBucketlists();
      setIsLoading(false);
      return;
    }

    async function fetchData() {
      try {
        const res = await get(`/api/poi-itin/${id}/`, {
          cancelToken: source.cancelToken,
          headers: {
            Authorization: `Token ${auth.token}`,
          },
        });
        setItinPoiList(res.data);
        setItineraryName(res.data[0].trip.name);
      } catch (e) {
        const message =
          e.response?.data?.message ||
          "Terjadi suatu kesalahan, silahkan coba lagi.";
        alert.error(message);
        setError(e);
      }
      setIsLoading(false);
    }
    fetchData();
    getBucketlists();
    

    return () => {
      source.cancel();
    };
    // eslint-disable-next-line
  }, [id, auth, history, alert, props.mode, poiTemp]);

  function deletePoi (poi) {
    if (itinPoiList.length === 1) {
      alert.info("Itinerary setidaknya harus memiliki satu POI");
      return;
    }
    const poiId = poi.id;
    setItinPoiList(itinPoiList.filter((itinPoi) => itinPoi.poi.id !== poiId));
    setAllPois(allPois.filter((allPois) => allPois.poi_id !== poiId));
    setEditedPois(editedPois.filter(edited_poi => edited_poi.poi_id !== poiId));
    setBucketlists([...bucketlists, poi]);
    setDeletedPoi([...deletedPoi, { poi_id: poiId }]);
  }

  const filter = () => {
    bucketlists.sort(function (a, b) {
      var keyA = Date.parse(a.start_time),
        keyB = Date.parse(b.start_time);
      // Compare the 2 dates
      if (keyA < keyB) return -1;
      if (keyA > keyB) return 1;
      return 0;
    });
  };

  const addPoi = (poi, startTime, endTime) => {
    const wibFormatter = new Intl.DateTimeFormat("id", {
      dateStyle: "full",
      timeStyle: "short",
    });
    const start_time_formatted = wibFormatter.format(startTime);
    const end_time_formatted = wibFormatter.format(endTime);
    const addPoiObj = {
      id: poi.id,
      start_time: start_time_formatted,
      end_time: end_time_formatted,
      poi: poi,
    };
    const newItin = [...itinPoiList, addPoiObj];
    setItinPoiList(newItin);
    setBucketlists(bucketlists.filter((itinPoi) => itinPoi.id !== poi.id));

    if (props.mode === ItinDetailPageMode.edit) {
      setAddedPoi([
        ...addedPoi,
        {
          poi_id: poi.id,
          start_time: start_time_formatted,
          end_time: end_time_formatted,
        },
      ]);
    }
  };

  async function submitCreate() {
    if (itineraryName.length === 0 || !itineraryName.trim()) {
      alert.info("Nama Itinerary tidak boleh kosong!");
      return;
    }
    try {
      setIsLoading(true);
      await post(
        "/api/create-itin/",
        {
          name: itineraryName,
          itinerary: itinPoiList.map((poi) => ({
            poi_id: poi.poi.id,
            start_time: poi.start_time || poi.start,
            end_time: poi.end_time || poi.end,
          })),
        },
        {
          headers: {
            Authorization: `Token ${auth.token}`,
          },
        }
      );
      history.push(`/itin`);
      alert.success(`Sukses membuat Itinerary ${itineraryName}`);
    } catch (e) {
      const message =
        e.response?.data?.message ||
        "Terjadi suatu kesalahan, silahkan coba lagi.";
      alert.error(message);
    }
    setIsLoading(false);
  }

  async function submitEdit() {
    let body = {}
        body["id_trip"] = id
        body["name"] = itineraryName
        body["del_itin"] = deletedPoi
        body["edit_date"] = editedPois
        body["add_poi"] = addedPoi
    if (checkConflictAll()){

    }
    else {
      try {
        setIsLoading(true);
        await post(
          "/api/edit-itin/",
          body,
          {
            headers: {
              Authorization: `Token ${auth.token}`,
            },
          }
        );
        history.push(`/itin/${id}`);
        alert.success(`Sukses melakukan edit untuk Itinerary ${itineraryName}`);
      } catch (e) {
        console.log("error while POST /api/edit-itin/", e);
        const message =
          e.response?.data?.message ||
          "Terjadi suatu kesalahan, silahkan coba lagi.";
        alert.error(message);
      }
      setIsLoading(false);
    }
  }

  async function submit() {
    switch (props.mode) {
      case ItinDetailPageMode.edit:
        return submitEdit();
      case ItinDetailPageMode.create:
        return submitCreate();
      default:
        return null;
    }
  }

  const strToDate = (str) => {
    var months = ['','Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember']
    var start_time = str.split(" ")
    var start = start_time.slice(1,5)
    start[1] = months.indexOf(start[1]).toString().length === 1 ? '0'.concat(months.indexOf(start[1]).toString()) : months.indexOf(start[1]).toString()
    start[3] = 'T'.concat(start[3].includes(".") ? start[3].replace(".", ":").concat(":00") : start[3].concat(":00"))
    var date = start.slice(0,3).reverse().join("-")
    var date_time = date.concat(start[3])
    return date_time.toString()
  } 

  // const isInteractive = props.mode !== ItinDetailPageMode.read;
  const isEdit = props.mode === ItinDetailPageMode.edit;

  async function copyShareUrlToClipboard(e) {
    const username = auth.user.username;
    const shareUrl = generateShareItinUrl(username, id);
    navigator.clipboard.writeText(shareUrl);
    alert.success(`Sukses menyalin link itinerary ke clipboard`);
  }

  const isInteractive =
    props.mode === ItinDetailPageMode.create ||
    props.mode === ItinDetailPageMode.edit;

  if (isLoading || error) {
    return (
      <div className="d-flex justify-content-center align-self-center align-items-center w-100 h-100">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  return (
    <div className="itin-list-container">
      <div className="itin-list-container-title flex-column flex-md-row mb-4">
        {!isInteractive && (
          <div>
            <h3 className="mb-2">Itinerary {itineraryName}</h3>
            {props.mode === ItinDetailPageMode.public && (
              <div className="mb-4" data-testid="created-by-dom">
                <span>dibuat oleh </span>
                <span className="font-weight-bold">{createdBy}</span>
              </div>
            )}
          </div>
        )}
        {isInteractive && (
          <input
            type="text"
            className="form-control itinerary-name-input h3"
            value={itineraryName}
            placeholder="Tulis nama ITIN .."
            onChange={(e) => setItineraryName(e.target.value)}
            data-testid="edit-itin-input"
          />
        )}
        {isInteractive && (
          <button
            className="primary-button save-itinerary-button"
            onClick={submit}
            disabled={isLoading}
            data-testid="submit-itin-button"
          >
            Simpan Itinerary
          </button>
        )}
        {props.mode === ItinDetailPageMode.read && (
          <div className="d-flex flex-column flex-sm-row">
            <button
              to={`/itin/${id}/edit`}
              className="primary-button share-button mr-sm-4 font-weight-bold"
              data-testid="share-itin-button"
              onClick={copyShareUrlToClipboard}
            >
              <ShareIcon />
              <span>Salin Link Itinerary</span>
            </button>
            <Link
              to={`/itin/${id}/edit`}
              className="secondary-button edit-button mt-4 mb-4 mt-sm-0 mb-sm-0"
              data-testid="edit-itin-button"
            >
              <EditIcon />
              <span>Edit</span>
            </Link>
          </div>
        )}
      </div>

      {itinPoiList.map((itinRow, i) => {
        const prevStartDate =
          i === 0
            ? null
            : extractDateFromDateTimeString(itinPoiList[i - 1].start_time);
        const startDate = extractDateFromDateTimeString(itinRow.start_time);
        return (
          <>
            {prevStartDate !== startDate && (
              <div
                key={`itin-start-date-${i}`}
                className="headline itinerary-start-date"
              >
                {startDate}
              </div>
            )}
            <ItinPoiItemComponent
              key={`itin-poi-item-${itinRow.id}`}
              showDeleteButton={isInteractive}
              showEditDateForm={isEdit}
              onDelete={() => deletePoi(itinRow.poi)}
              editedPois = {editedPois}
              setEditedPois = {setEditedPois}
              convert={(date) => strToDate(date)}
              allPois = {allPois}
              setAllPois = {setAllPois}
              {...itinRow}
            />
          </>
        );
      })}

      {isInteractive && (
        <div>
          <h3 id="tambah-title">Tambah dari Bucket List</h3>
          <hr className="add-bucket" />
        </div>
      )}

      {isInteractive &&
        (bucketlists.length !== 0 ? (
          bucketlists.map((poi) => {
            return (
              <div className="each-bucket">
                <BucketListAddition
                  key={`poi-item-${poi.id}`}
                  poi={poi}
                  addPoi={addPoi}
                  filter={filter}
                />
                <hr />
              </div>
            );
          })
        ) : (
          <div className="mb-5 h3 font-weight-normal">No Bucketlists</div>
        ))}
    </div>
  );
}

ItinPoiItemComponent.propTypes = {
  showDeleteButton: PropTypes.bool,
};

function ItinPoiItemComponent(props) {

  const [startTime, setStartTime] = useState(Number.isNaN(Date.parse(props.start_time))? new Date(props.convert(props.start_time)) : Date.parse(props.start_time));
  const [endTime, setEndTime] = useState(Number.isNaN(Date.parse(props.end_time))? new Date(props.convert(props.end_time)) : Date.parse(props.end_time));

  const getEditedElement = () => {
    const wibFormatter = new Intl.DateTimeFormat('id', { dateStyle: 'full', timeStyle: 'short' });
    let editedElement = {};
    editedElement['poi_id'] = props.poi.id;
    editedElement['start_time'] = wibFormatter.format(startTime);
    editedElement['end_time'] = wibFormatter.format(endTime);

    return editedElement;
  }

  const getEditedListIdx = (id) => {
    let idx = -1;
    for(var i = 0; i < props.editedPois.length; i++) {
      if (props.editedPois[i].poi_id === id) {
        idx = i;
        break;
      }
    }
    return idx;
  }

  const getAllPoisIdx = (id) => {
    let idx = -1;
    for(var i = 0; i < props.allPois.length; i++) {
      if (props.allPois[i].poi_id === id) {
        idx = i;
        break;
      }
    }
    return idx;
  }

  const updateEditedList = () => {
    const isEdited = ( getEditedListIdx(props.poi.id) !== -1);
    props.setEditedPois(prev => isEdited
      ?
      [...prev.filter(edited_poi => edited_poi.poi_id !== props.poi.id), getEditedElement()]
      :
      [...prev, getEditedElement()]);
  }

  const updateAllPois = () => {
    const isEdited = ( getAllPoisIdx(props.poi.id) !== -1);
    props.setAllPois(prev => isEdited
      ?
      [...prev.filter(edited_poi => edited_poi.poi_id !== props.poi.id), getEditedElement()]
      :
      [...prev, getEditedElement()]);
  }

  const onChangeTime =  () => {
    updateAllPois()
    if ((startTime.toString() !== new Date(props.convert(props.start_time)).toString() || endTime.toString() !== new Date(props.convert(props.end_time)).toString())
    && ( startTime.toString() !== Date.parse(props.start_time).toString() || endTime.toString() !== Date.parse(props.end_time).toString())
    ){
      updateEditedList()
    }
  }

  useEffect(() => {
   onChangeTime();
  }, [startTime, endTime]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return (
    <div className="itin-poi-item-container" testid="itin-poi-item">
      <img src={props.poi.image_url} alt={`itin-poi-item-${props.id}`} />
      <div className="itin-poi-item-details-container flex-column flex-md-row align-items-start align-items-md-center">
        <div>
          <div className="itin-poi-item-name headline">{props.poi.name}</div>
          <FiveStarComponent
            className="itin-poi-review-container"
            editable={false}
            value={props.poi.rating.rating}
          />
          <PriceLabel
            min={props.poi.min_spending}
            max={props.poi.max_spending}
            suffix=" / orang"
          />
           {!props.showEditDateForm && 
          <div className="itin-poi-item-time">
            {props.start_time} - {props.end_time}
          </div>}
          {props.showEditDateForm && (
          <div className="mt-4">
          <DatePicker
            className="form-control mr-2"
            selected={startTime}
            onChange={date => setStartTime(date)}
            showTimeSelect
            timeFormat="HH:mm"
            dateFormat="MMMM d, yyyy h:mm aa"
          />

          -

          <DatePicker
            className="form-control ml-2"
            selected={endTime}
            onChange={date => setEndTime(date)}
            showTimeSelect
            timeFormat="HH:mm"
            dateFormat="MMMM d, yyyy h:mm aa"
          />
          </div>
          )}
        </div>
        {props.showDeleteButton && (
          <button className="delete-button" onClick={props.onDelete}>
            <DeleteIcon />
            <span>Hapus</span>
          </button>
        )}
      </div>
    </div>
  );
}

